/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => {
    const pagination = getProductListDataViewProperty(state, "pagination");
    const correctedQuery = getProductListDataViewProperty(state, "correctedQuery");
    const { productFilters } = state.pages.productList;
    // XNG-Changes
    const pageNumber = state.pages.productList?.filteredApiParameter?.page ?? 1;
    return {
        pageSize: pagination?.pageSize, // xng-change
        totalItemCount: pagination?.totalItemCount,
        query: correctedQuery || productFilters.query,
        isEstimatedItemCount: getSettingsCollection(state).searchSettings.searchProvider === "Commerce Search v3",
        // XNG-Changes
        pageNumber,
    };
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface ProductListProductCountStyles {
    wrapper?: InjectableCss;
    countText?: TypographyPresentationProps;
    itemsText?: TypographyPresentationProps;
    queryText?: TypographyPresentationProps;
}

export const productCountStyles: ProductListProductCountStyles = {
    wrapper: {
        css: css`
            margin-top: 10px;
        `,
    },
    countText: {
        css: css`
            white-space: nowrap;
        `,
    },
    queryText: {
        css: css`
            white-space: nowrap;
            font-weight: bold;
        `,
    },
};

const styles = productCountStyles;

const ProductListProductCount: FC<Props> = ({
    totalItemCount,
    query,
    pageSize,
    isEstimatedItemCount,
    // XNG-Changes
    pageNumber,
}) => {
    if (!totalItemCount) {
        return null;
    }

    const itemsText =
        // XNG-Changes: update text
        (totalItemCount === 1 ? " product" : " products") +
        (isEstimatedItemCount ? " (estimated)" : "") +
        (query ? " for " : "");

    // XNG-Changes: Update the template to make it match the design.
    const productPageStart = pageSize && pageSize * (pageNumber - 1) + 1;
    const productPageEnd = pageSize && totalItemCount > pageSize * pageNumber ? pageSize * pageNumber : totalItemCount;
    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.countText} data-test-selector="productListCountText">
                {translate("Showing ")}
                <Typography>{`${productPageStart}-${productPageEnd} `}</Typography>
                {translate("of")}
                <Typography> {totalItemCount}</Typography>
            </Typography>
            <Typography {...styles.itemsText}>{translate(itemsText)}</Typography>
            {query && (
                <Typography {...styles.queryText} data-test-selector="productListQueryText">
                    {query}
                </Typography>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductListProductCount),
    definition: {
        group: "Product List",
        displayName: "Product List Count",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
